import { DataAggregationLevel, InsightsReportType, ReportSchedule } from 'apis/merchant-gateway/insights';
import { useTelemetryViewEvent } from 'common/hooks';
import { EntityType } from 'common/types';
import {
  businessInsightsInteractionClickChartSource,
  businessInsightsInteractionClickDatePicker,
  businessInsightsV2CustomerBaseView,
  businessInsightsV2InteractionClickSwitchToOldInsights,
  businessInsightsV2MenuView,
  businessInsightsV2OperationsView,
  businessInsightsV2PerformanceView,
  businessInsightsV2RatingsReviewsInteractionClickFilterRatings,
  businessInsightsV2RatingsReviewsInteractionHoverOffer,
  businessInsightsV2RatingsReviewsInteractionSendOffer,
  businessInsightsV2RatingsReviewsView,
  businessInsightsV2ReportBuilderDataChooseDataOptions,
  businessInsightsV2ReportBuilderDataChooseVenues,
  businessInsightsV2ReportBuilderInteractionChooseAggregation,
  businessInsightsV2ReportBuilderInteractionChooseCadence,
  businessInsightsV2ReportBuilderInteractionChooseReportType,
  businessInsightsV2ReportBuilderView,
  businessInsightsV2View,
  businessInsightsV2InteractionCreateReport,
  businessInsightV2ReportBuilderInteractionCreateReport,
  DateLengthChosen,
  InsightReportType,
  ReportAggregationChosen,
  ReportCadenceChosen,
  SourceTitle,
  SourceTitleValueType,
  TargetContentDatePicker,
  TargetContentDatePickerValueType,
  TargetContentFilterRatingsValueType,
} from 'core/telemetry/avo/Avo';

import { InsightEntity } from './types';

const insightsReportMapping = {
  [InsightsReportType.OPERATIONS]: InsightReportType.OPERATIONS,
  [InsightsReportType.MOST_SOLD_ITEMS]: InsightReportType.MOST_SOLD_ITEMS,
  [InsightsReportType.RATINGS]: InsightReportType.RATINGS,
  [InsightsReportType.PERFORMANCE]: InsightReportType.PERFORMANCE,
};

const dataAggregationLevelMapping = {
  [DataAggregationLevel.DAILY]: ReportAggregationChosen.DAILY,
  [DataAggregationLevel.HOURLY]: ReportAggregationChosen.HOURLY,
  [DataAggregationLevel.WEEKLY]: ReportAggregationChosen.WEEKLY,
  [DataAggregationLevel.MONTHLY]: ReportAggregationChosen.MONTHLY,
};

const reportScheduleMapping = {
  [ReportSchedule.DAILY]: ReportCadenceChosen.DAILY,
  [ReportSchedule.WEEKLY]: ReportCadenceChosen.WEEKLY,
  [ReportSchedule.MONTHLY]: ReportCadenceChosen.MONTHLY,
};

export const telemetryClickDatePickerInHomeInsightPage = ({
  targetContentDatePicker,
  dateLengthChosen,
}: {
  targetContentDatePicker?: TargetContentDatePickerValueType;
  dateLengthChosen: DateLengthChosen;
}) => {
  businessInsightsInteractionClickDatePicker({
    targetContentDatePicker,
    dateLengthChosen,
  });
};

export const telemetrySelectPerformanceChartInHomeInsightPage = (sourceTitle: SourceTitleValueType) => {
  businessInsightsInteractionClickChartSource({
    sourceTitle,
  });
};

export const telemetryClickFilterRatingsInInsightsV2RatingsReviews = (
  targetContentFilterRatings: TargetContentFilterRatingsValueType = 'filter_ratings_value_1',
) => {
  businessInsightsV2RatingsReviewsInteractionClickFilterRatings({
    targetContentFilterRatings,
  });
};

export const useTelemetryBusinessInsightV2View = (entityType: EntityType) => {
  useTelemetryViewEvent('businessInsightsV2View', { viewType: entityType }, (durationMs, context) =>
    businessInsightsV2View({ durationMs, ...context }),
  );
};

export const useTelemetryBusinessInsightsV2PerformanceView = (entityType: EntityType) => {
  useTelemetryViewEvent('businessInsightsV2PerformanceView', { viewType: entityType }, (durationMs, context) =>
    businessInsightsV2PerformanceView({ durationMs, ...context }),
  );
};

export const useTelemetryBusinessInsightsV2OperationsView = (entityType: EntityType) => {
  useTelemetryViewEvent('businessInsightsV2OperationsView', { viewType: entityType }, (durationMs, context) =>
    businessInsightsV2OperationsView({ durationMs, ...context }),
  );
};

export const useTelemetryBusinessInsightsV2CustomerBaseView = (entityType: EntityType) => {
  useTelemetryViewEvent('businessInsightsV2CustomerBaseView', { viewType: entityType }, (durationMs, context) =>
    businessInsightsV2CustomerBaseView({ durationMs, ...context }),
  );
};

export const useTelemetryBusinessInsightsV2MenuView = (entityType: EntityType) => {
  useTelemetryViewEvent('businessInsightsV2MenuView', { viewType: entityType }, (durationMs, context) =>
    businessInsightsV2MenuView({ durationMs, ...context }),
  );
};

export const useTelemetryInsightsV2RatingsReviewsView = (entityType: EntityType) => {
  useTelemetryViewEvent('businessInsightsV2RatingsReviewsView', { viewType: entityType }, (durationMs, context) =>
    businessInsightsV2RatingsReviewsView({ durationMs, ...context }),
  );
};

export const telemetryBusinessInsightsV2SwitchToOld = (viewType: InsightEntity['type']) => {
  businessInsightsV2InteractionClickSwitchToOldInsights({ viewType });
};

export const telemetryRatingsAndReviewsSendOfferHover = () => {
  businessInsightsV2RatingsReviewsInteractionHoverOffer();
};

export const telemetryRatingsAndReviewsSendOfferClick = () => {
  businessInsightsV2RatingsReviewsInteractionSendOffer();
};

export const telemetryBusinessInsightsV2CreateReport = () => {
  businessInsightsV2InteractionCreateReport();
};

export const telemetryBusinessInsightsV2ReportBuilderChooseAggregation = (
  dataAggregationLevel: DataAggregationLevel,
) => {
  businessInsightsV2ReportBuilderInteractionChooseAggregation({
    reportAggregationChosen: dataAggregationLevelMapping[dataAggregationLevel],
  });
};

export const telemetryBusinessInsightsV2ReportBuilderChooseVenues = (venueIdsList: string[]) => {
  businessInsightsV2ReportBuilderDataChooseVenues({ venuesChosenReportBuilder: venueIdsList });
};

export const telemetryBusinessInsightsV2ReportBuilderCreateReportSubmitForm = () => {
  businessInsightV2ReportBuilderInteractionCreateReport();
};

export const telemetryBusinessInsightsV2ReportBuilderChooseReportType = (insightReportType: InsightsReportType) => {
  if (insightReportType === InsightsReportType.SETTLEMENTS) {
    return;
  }

  businessInsightsV2ReportBuilderInteractionChooseReportType({
    insightReportType: insightsReportMapping[insightReportType],
  });
};

export const telemetryBusinessInsightsV2ReportBuilderChooseCadence = (reportScheduleChosen: ReportSchedule) => {
  businessInsightsV2ReportBuilderInteractionChooseCadence({
    reportCadenceChosen: reportScheduleMapping[reportScheduleChosen],
  });
};

export const useTelemetryInsightsV2ReportBuilderView = () => {
  useTelemetryViewEvent('businessInsightsV2ReportBuilderView', null, durationMs =>
    businessInsightsV2ReportBuilderView({ durationMs }),
  );
};

export const telemetryBusinessInsightsV2ReportBuilderChooseDataOptions = (dataOptionsList: string[]) => {
  businessInsightsV2ReportBuilderDataChooseDataOptions({ dataOptionsChosenReportBuilder: dataOptionsList });
};
export { SourceTitle as ChartCategoryTelemetryEvent };
export { TargetContentDatePicker as PredefinedDateRangeOptions };
